import ReservationsPage from './pages/members/ReservationsPage';
import { usePageMeta } from './hooks/usePageMeta';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useFeature, UpgradeHint } from './hooks/useFeature';
import { useEditionStore } from './store/edition.store';
import { useAuthStore } from './store/auth.store';
import type { FeatureKey } from '@pokerroom/types';
import LoginPage from './pages/auth/LoginPage';
import Setup2FAPage from './pages/auth/Setup2FAPage';
import ChangePasswordPage from './pages/auth/ChangePasswordPage';
import SetupWizardPage from './pages/auth/SetupWizardPage';
import DashboardPage from './pages/DashboardPage';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminRolesPage from './pages/admin/AdminRolesPage';
import PermissionMatrixPage from './pages/admin/PermissionMatrixPage';
import AdminLogsPage from './pages/admin/AdminLogsPage';
import UserStoryMapPage from './pages/admin/UserStoryMapPage';
import TimerPreviewPage from './pages/admin/TimerPreviewPage';
import VenueSettingsPage from './pages/admin/VenueSettingsPage';
import TerminologyPage from './pages/admin/TerminologyPage';
import ProtectedRoute from './components/layout/ProtectedRoute';
import AppLayout from './components/layout/AppLayout';
// §3 會員
import MemberListPage from './pages/members/MemberListPage';
import MemberPortalEditorPage from './pages/members/MemberPortalEditorPage';
import MemberDetailPage from './pages/members/MemberDetailPage';
import MemberCardScanPage from './pages/members/MemberCardScanPage';
import MemberFormPage from './pages/members/MemberFormPage';
import BatchImportPage from './pages/members/BatchImportPage';
import CheckInPage from './pages/members/CheckInPage';
import QRCheckInPage from './pages/members/QRCheckInPage';
import PointsConfigPage from './pages/members/PointsConfigPage';
import WalletConfigPage from './pages/members/WalletConfigPage';
// §4 帳務
import DailyOpeningPage from './pages/accounting/DailyOpeningPage';
import ExpenseEntryPage from './pages/accounting/ExpenseEntryPage';
import DailySettlementPage from './pages/accounting/DailySettlementPage';
import PrintReportPage from './pages/accounting/PrintReportPage';
import EmailSettingsPage from './pages/accounting/EmailSettingsPage';
import ReportsPage from './pages/accounting/ReportsPage';
// §2 錦標賽
import TournamentListPage from './pages/tournament/TournamentListPage';
import TournamentCreatePage from './pages/tournament/TournamentCreatePage';
import TournamentDetailPage from './pages/tournament/TournamentDetailPage';
import TournamentTimerPage from './pages/tournament/TournamentTimerPage';
import PlayerRegistrationPage from './pages/tournament/PlayerRegistrationPage';
import EliminationPage from './pages/tournament/EliminationPage';
import PayoutPage from './pages/tournament/PayoutPage';
import TimedSettlementPage from './pages/tournament/TimedSettlementPage';
import BlindStructureEditorPage from './pages/tournament/BlindStructureEditorPage';
import TournamentTemplatesPage from './pages/tournament/TournamentTemplatesPage';
import TimedSettlementLevelsPage from './pages/tournament/TimedSettlementLevelsPage';
import TournamentCalendarPage from './pages/tournament/TournamentCalendarPage';
// §5 電視顯示
import DisplayManagerPage from './pages/display/DisplayManagerPage';
import TVScreen from './pages/display/TVScreen';
import TVLauncher from './pages/display/TVLauncher';
import TVAcceptance from './pages/display/TVAcceptance';
// §6 場管 + 荷官（全螢幕，不使用 AppLayout）
import FloorManagerPage from './pages/floor/FloorManagerPage';
import DealerPage from './pages/dealer/DealerPage';
// §9 品牌設定 + 牌桌管理 + 頁面設定
import BrandingPage from './pages/admin/BrandingPage';
import PageSettingsPage from './pages/admin/PageSettingsPage';
import TvAppSetupPage from './pages/admin/TvAppSetupPage';
import DevToolsPage from './pages/admin/DevToolsPage';
import VenueManagementPage from './pages/admin/VenueManagementPage';
import TableManagementPage from './pages/tables/TableManagementPage';
import PayoutRuleSettingsPage from './pages/tournament/PayoutRuleSettingsPage';
import TournamentPayoutRulePage from './pages/tournament/TournamentPayoutRulePage';

/** Layout route that gates children by feature flag */
function FeatureLayout({ feature }: { feature: FeatureKey }) {
  const enabled = useFeature(feature);
  if (!enabled)
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <UpgradeHint feature={feature} />
      </div>
    );
  return <Outlet />;
}

/** Lite: redirect / to /tournament (non-admin only); Business: show Dashboard */
function LiteDashboardRedirect() {
  const isLite = useEditionStore((s) => s.isLite());
  const user = useAuthStore((s) => s.user);
  if (isLite && !user?.isSystemUser) return <Navigate to="/tournament" replace />;
  return <DashboardPage />;
}

function App() {
  usePageMeta();
  return (
    <Routes>
      {/* 公開路由 */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/setup-2fa" element={<Setup2FAPage />} />
      <Route path="/auth/change-password" element={<ChangePasswordPage />} />
      <Route path="/setup" element={<SetupWizardPage />} />
      <Route path="/admin/user-story-map" element={<UserStoryMapPage />} />
      {/* §5 電視顯示（Token URL 公開，不需登入） */}
      <Route path="/display/screen/:token" element={<TVScreen />} />
      <Route path="/tv" element={<TVLauncher />} />
      <Route path="/tv/acceptance" element={<TVAcceptance />} />
      <Route path="/tv/:venue/:slug" element={<TVScreen />} />
      <Route path="/tv/:slug" element={<TVScreen />} />

      {/* 受保護路由 — 認證 guard */}
      <Route element={<ProtectedRoute />}>
        {/* §6 場管 + 荷官 + 賽事時鐘：全螢幕操作介面，不加 Sidebar */}
        <Route path="/floor/:tournamentId" element={<FloorManagerPage />} />
        <Route path="/dealer/:tournamentId/:tableId" element={<DealerPage />} />
        {/* 時鐘頁自帶 header 且版面是 h-screen；套上 AppLayout 會被側邊欄與
            TopHeader 各吃掉 240px 寬與 64px 高，手機／平板上底部控制列會被裁掉 */}
        <Route path="/tournament/:id/timer" element={<TournamentTimerPage />} />

        {/* 一般管理介面：使用 AppLayout (Sidebar) */}
        <Route element={<AppLayout />}>
          <Route path="/" element={<LiteDashboardRedirect />} />

          {/* §1 使用者管理 */}
          <Route path="/admin/users" element={<AdminUsersPage />} />
          <Route path="/admin/roles" element={<AdminRolesPage />} />
          <Route path="/admin/permission-matrix" element={<PermissionMatrixPage />} />
          <Route path="/admin/logs" element={<AdminLogsPage />} />

          {/* §8 多語言 */}
          <Route path="/admin/terminology" element={<TerminologyPage />} />
          <Route path="/admin/email-settings" element={<EmailSettingsPage />} />
          <Route element={<FeatureLayout feature="member.points" />}>
            <Route path="/admin/points-config" element={<PointsConfigPage />} />
            <Route path="/admin/points-config/wallet" element={<WalletConfigPage />}>
              <Route index element={null} />
              <Route path="vouchers" element={null} />
              <Route path="passes" element={null} />
              <Route path="monthly" element={null} />
            </Route>
          </Route>
          <Route element={<FeatureLayout feature="display.layout-editor" />}>
            <Route path="/admin/timer-preview" element={<TimerPreviewPage />} />
          </Route>
          <Route path="/admin/venue" element={<VenueSettingsPage />} />
          <Route path="/admin/branding" element={<BrandingPage />} />
          <Route path="/admin/page-settings" element={<PageSettingsPage />} />
          <Route path="/admin/tv-app" element={<TvAppSetupPage />} />
          <Route path="/admin/dev-tools" element={<DevToolsPage />} />
          <Route path="/admin/venues" element={<VenueManagementPage />} />

          {/* 牌桌管理 — requires floor.console */}
          <Route element={<FeatureLayout feature="floor.console" />}>
            <Route path="/tables" element={<TableManagementPage />} />
          </Route>

          {/* §3 會員 — requires member.enabled */}
          <Route element={<FeatureLayout feature="member.enabled" />}>
            <Route path="/members" element={<MemberListPage />} />
            <Route path="/members/reservations" element={<ReservationsPage />} />
            <Route path="/admin/member-portal-editor" element={<MemberPortalEditorPage />} />
            <Route path="/members/scan" element={<MemberCardScanPage />} />
            <Route path="/members/create" element={<MemberFormPage />} />
            <Route path="/members/import" element={<BatchImportPage />} />
            <Route path="/members/:id" element={<MemberDetailPage />} />
            <Route path="/members/:id/edit" element={<MemberFormPage />} />
            <Route path="/check-in" element={<CheckInPage />} />
            <Route path="/check-in/qr" element={<QRCheckInPage />} />
          </Route>

          {/* §4 帳務 — requires accounting.enabled */}
          <Route element={<FeatureLayout feature="accounting.enabled" />}>
            <Route path="/accounting/open" element={<DailyOpeningPage />} />
            <Route path="/accounting/expenses" element={<ExpenseEntryPage />} />
            <Route path="/accounting/settle" element={<DailySettlementPage />} />
            <Route path="/accounting/report" element={<PrintReportPage />} />
            <Route path="/accounting/reports" element={<ReportsPage />} />
          </Route>

          {/* §2 錦標賽 */}
          <Route path="/tournament" element={<TournamentListPage />} />
          <Route path="/tournament/create" element={<TournamentCreatePage />} />
          <Route path="/tournament/:id" element={<TournamentDetailPage />} />
          <Route path="/tournament/:id/register" element={<PlayerRegistrationPage />} />
          <Route path="/tournament/:id/eliminate" element={<EliminationPage />} />
          <Route path="/tournament/:id/payout" element={<PayoutPage />} />
          <Route path="/tournament/:id/timed-settlement" element={<TimedSettlementPage />} />
          <Route path="/tournament/:id/payout-rule" element={<TournamentPayoutRulePage />} />
          <Route path="/tournament/blind-structures" element={<BlindStructureEditorPage />} />
          <Route
            path="/tournament/timed-settlement-levels"
            element={<TimedSettlementLevelsPage />}
          />
          <Route path="/tournament/templates" element={<TournamentTemplatesPage />} />
          <Route path="/tournament/payout-rules" element={<PayoutRuleSettingsPage />} />
          <Route path="/tournament/calendar" element={<TournamentCalendarPage />} />

          {/* §5 電視顯示管理 */}
          <Route path="/display" element={<DisplayManagerPage />} />
        </Route>
      </Route>

      {/* 預設重定向 */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
